import React, { useEffect, useMemo, useState } from "react";
import Page from "components/page/Page";
import { t } from "i18next";
import {
  GetEmailCampaignAnalytics,
  GetEmailCampaignAnalyticsRecipients,
  GetEmailCampaignAnalyticsRecipientsExport,
  GetEmailCampaigns,
  IEmailCampaign,
  IEmailCampaignAnalytics,
  IEmailCampaignAnalyticsRecipient,
  PostEmailCampaignAnalyticsRecipientsResend,
  TEmailCampaignAnalyticsMetric,
} from "api/rpc/2024-04/facilityAdmin/marketing/emailCampaigns";
import Search from "components/search/Search";
import DataTable, { HeaderType } from "components/dataTable/DataTable";
import { useHistory, useParams } from "react-router";
import { StatusCode } from "api/protocols";
import { showError, showSuccess } from "redux/actions/ui";
import { useDispatch } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Badge } from "components/badge/Badge";
import { ButtonNew } from "components/buttonNew";
import { downloadCsv } from "helpers/Helpers";
import moment from "moment";
import snakeCase from "lodash/snakeCase";
import { faCircleUp } from "@fortawesome/pro-regular-svg-icons/faCircleUp";
import { TopLinksFilter } from "./TopLinksFilter";

export default function Recipients() {
  const dispatch = useDispatch();
  const history = useHistory();
  const { id: campaignId, metric } = useParams<{ id: string, metric: TEmailCampaignAnalyticsMetric }>();
  const [campaign, setCampaign] = useState<IEmailCampaign | null>(null);
  const [analytics, setAnalytics] = useState<IEmailCampaignAnalytics | null>(null);
  const [recipients, setRecipients] = useState<IEmailCampaignAnalyticsRecipient[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);
  const [sort, setSort] = useState<{ key?: string; direction?: "asc" | "desc" }>({});
  const [selectedTopLink, setSelectedTopLink] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [resendingId, setResendingId] = useState<number | null>(null);
  const limit = 10;

  async function loadCampaign() {
    const resp = await GetEmailCampaigns({ id: campaignId }, false);
    if (resp.status === StatusCode.OK) {
      if (resp.data.length === 0) {
        dispatch(showError('Could not find email campaign'));
        history.push('/admin/email-marketing/campaigns');
        return;
      }
      const campaign = resp.data.pop();
      if (campaign === undefined) {
        return;
      }

      setCampaign(campaign);
    }
  }

  async function loadAnalytics() {
    const resp = await GetEmailCampaignAnalytics(
      {
        id: Number(campaignId),
      },
      false,
    );

    if (resp.status !== StatusCode.OK || !resp.data) {
      dispatch(showError(resp.message || t("Unable to load campaign analytics.")));
      return;
    }

    setAnalytics({
      ...resp.data,
      summary: {
        ...resp.data.summary,
      },
      funnel: {
        ...resp.data.funnel,
      },
      top_links: resp.data.top_links ?? [],
      booking_categories: resp.data.booking_categories ?? [],
      member_types: resp.data.member_types ?? [],
    });

    if (metric === "top_links") {
      setSelectedTopLink(current => current ?? resp.data.top_links?.[0]?.url ?? null);
    }
  }

  useEffect(() => {
    void loadCampaign();
    void loadAnalytics();
  }, []);

  useEffect(() => {
    if (!analytics) {
      return;
    }

    let cancelled = false;

    async function loadRecipients() {
      setLoading(true);
      const resp = await GetEmailCampaignAnalyticsRecipients(
        {
          id: Number(campaignId),
          metric,
          page: Math.floor(offset / limit) + 1,
          search: search || undefined,
          sort_by: sort.key,
          sort_direction: sort.direction,
          url: metric === "top_links" ? selectedTopLink ?? undefined : undefined,
          send_batch_id: analytics?.send_batch_id ?? undefined,
          per_page: limit,
        },
        false,
      );

      if (cancelled) {
        return;
      }

      setLoading(false);
      if (resp.status !== StatusCode.OK) {
        dispatch(showError(resp.message || t("Unable to load campaign recipients.")));
        return;
      }

      setRecipients(resp.data?.recipients?.data ?? []);
      setTotal(resp.data?.recipients?.total ?? 0);
    }

    void loadRecipients();

    return () => {
      cancelled = true;
    };
  }, [analytics, campaignId, dispatch, metric, offset, refreshKey, search, selectedTopLink, sort]);

  function pageTitle(): string {
    switch (metric) {
      case 'delivered':
        return 'Delivered recipients';
      case 'opened':
        return 'Opened recipients';
      case 'bounced':
        return 'Bounced recipients';
      default:
        return 'View recipients';
    }
  }

  function pageSubtitle(): string {
    switch (metric) {
      case 'delivered':
        return 'View and manage recipients whose email was successfully delivered.';
      case 'opened':
        return 'View and manage recipients who opened the email.';
      case 'bounced':
        return 'View and manage recipients whose email failed to deliver.';
      case 'funnel':
        return `View and manage recipients for ${campaign?.name}.`;
      case 'top_links':
        return 'View recipients for the top links clicked.';
      case 'member_types':
        return 'View and manage recipients to whom the email was successfully delivered.';

      default:
        return 'View recipients';
    }
  }

  function handleSearch(value: string) {
    setSearch(value);
    setOffset(0);
  }

  async function fetchExport() {
    const resp = await GetEmailCampaignAnalyticsRecipientsExport(
      {
        id: Number(campaignId),
        metric,
        search: search || undefined,
        sort_by: sort.key,
        sort_direction: sort.direction,
        url: metric === "top_links" ? selectedTopLink ?? undefined : undefined,
        send_batch_id: analytics?.send_batch_id ?? undefined,
      },
      true,
    );

    if (resp.status !== StatusCode.OK || typeof resp.data !== "string") {
      dispatch(showError(resp.message || t("Unable to export campaign recipients.")));
      return;
    }

    downloadCsv(resp.data, `${snakeCase(campaign?.name ?? "email_campaign")}_${metric}_recipients.csv`);
  }

  function formatTime(value: string | null): string {
    return value ? moment(value).format("h:mm A") : "—";
  }

  function recipientName(recipient: IEmailCampaignAnalyticsRecipient): string {
    const name = [recipient.first_name, recipient.last_name].filter(Boolean).join(" ");
    return name || recipient.email;
  }

  function recipientCell(recipient: IEmailCampaignAnalyticsRecipient) {
    return (
      <div>
        <div className="font-medium text-gray-900">{recipientName(recipient)}</div>
        <div className="text-xs text-gray-600">{recipient.email}</div>
      </div>
    );
  }

  function statusBadge(active: boolean, activeLabel: string, inactiveLabel: string) {
    return <Badge type={active ? "success" : "error"}>{t(active ? activeLabel : inactiveLabel)}</Badge>;
  }

  function bounceTypeBadge(bounceType: IEmailCampaignAnalyticsRecipient["bounce_type"]) {
    const isSoft = String(bounceType ?? "").toLowerCase().includes("soft");

    return (
      <Badge type={isSoft ? "warning" : "error"}>
        {t(bounceType || "Hard bounce")}
      </Badge>
    );
  }

  function canResend(recipient: IEmailCampaignAnalyticsRecipient) {
    return recipient.action === "resend_email" || recipient.action_label === "Resend email";
  }

  async function resendRecipient(recipient: IEmailCampaignAnalyticsRecipient) {
    if (!canResend(recipient) || resendingId !== null) {
      return;
    }

    setResendingId(recipient.id);
    const resp = await PostEmailCampaignAnalyticsRecipientsResend(
      {
        id: Number(campaignId),
        email_send_id: recipient.id,
      },
      true,
    );
    setResendingId(null);

    if (resp.status !== StatusCode.OK) {
      dispatch(showError(resp.message || t("Unable to resend campaign email.")));
      return;
    }

    dispatch(showSuccess(resp.message || t("Queued email campaign resend")));
    setRefreshKey(current => current + 1);
  }

  function actionCell(recipient: IEmailCampaignAnalyticsRecipient) {
    const label = t(recipient.action_label || "Resend email");

    if (!canResend(recipient)) {
      return <ButtonNew
        type="text"
        size="small"
      >
        <span className="font-medium text-gray-900">{label}</span>
      </ButtonNew>
;
    }

    return (
      <ButtonNew
        type="text"
        size="small"
        disabled={resendingId !== null}
        onClick={() => void resendRecipient(recipient)}
      >
        <span className="font-medium text-gray-900">{label}</span>
      </ButtonNew>
    );
  }

  const columns = useMemo<Array<HeaderType<IEmailCampaignAnalyticsRecipient>>>(() => {
    const recipient: HeaderType<IEmailCampaignAnalyticsRecipient> = {
      id: "first_name",
      label: t("Recipient"),
    };
    const segment: HeaderType<IEmailCampaignAnalyticsRecipient> = {
      id: "segment_type",
      label: t("Segment type"),
    };
    const action: HeaderType<IEmailCampaignAnalyticsRecipient> = { label: t("Action") };

    switch (metric) {
      case "delivered":
        return [
          recipient,
          segment,
          { id: "delivered_at", label: t("Delivered time") },
          { id: "opened_at", label: t("Open status") },
          action,
        ];
      case "opened":
        return [
          recipient,
          segment,
          { id: "opened_at", label: t("Open time") },
          action,
        ];
      case "bounced":
        return [
          recipient,
          segment,
          { id: "bounce_type", label: t("Bounce type") },
          { id: "bounce_reason", label: t("Bounce reason") },
          action,
        ];
      case "funnel":
        return [
          recipient,
          { id: "delivered_at", label: t("Delivery status") },
          { id: "opened_at", label: t("Open status") },
          { id: "clicked_at", label: t("Click status") },
          action,
        ];
      case "member_types":
        return [
          recipient,
          segment,
          { id: "member_type", label: t("Member type") },
          { id: "opened_at", label: t("Open time") },
          { label: t("Link clicked") },
        ];
      case "top_links":
        return [recipient];
      default:
        return [recipient];
    }
  }, [metric]);

  function renderCells(recipient: IEmailCampaignAnalyticsRecipient) {
    switch (metric) {
      case "delivered":
        return (
          <>
            <td>{recipientCell(recipient)}</td>
            <td>{recipient.segment_type ?? "—"}</td>
            <td>{formatTime(recipient.delivered_at)}</td>
            <td>{statusBadge(recipient.open_status === "Opened", "Opened", "Not opened")}</td>
            <td>{actionCell(recipient)}</td>
          </>
        );
      case "opened":
        return (
          <>
            <td>{recipientCell(recipient)}</td>
            <td>{recipient.segment_type ?? "—"}</td>
            <td>{formatTime(recipient.opened_at)}</td>
            <td>{actionCell(recipient)}</td>
          </>
        );
      case "bounced":
        return (
          <>
            <td>{recipientCell(recipient)}</td>
            <td>{recipient.segment_type ?? "—"}</td>
            <td>{bounceTypeBadge(recipient.bounce_type)}</td>
            <td>{recipient.bounce_reason ?? "—"}</td>
            <td>{actionCell(recipient)}</td>
          </>
        );
      case "funnel":
        return (
          <>
            <td>{recipientCell(recipient)}</td>
            <td>{statusBadge(recipient.delivery_status === "Delivered", "Delivered", "Not delivered")}</td>
            <td>{statusBadge(recipient.open_status === "Opened", "Opened", "Not opened")}</td>
            <td>{statusBadge(recipient.click_status === "Clicked", "Clicked", "Not clicked")}</td>
            <td>{actionCell(recipient)}</td>
          </>
        );
      case "member_types":
        return (
          <>
            <td>{recipientCell(recipient)}</td>
            <td>{recipient.segment_type ?? "—"}</td>
            <td>{recipient.member_type ?? "—"}</td>
            <td>{formatTime(recipient.opened_at)}</td>
            <td>{recipient.link_clicked ?? "—"}</td>
          </>
        );
      case "top_links":
      default:
        return <td>{recipientCell(recipient)}</td>;
    }
  }

  function handleSort(key: keyof IEmailCampaignAnalyticsRecipient, direction: "asc" | "desc") {
    setSort({ key: String(key), direction });
    setOffset(0);
  }

  function handlePage(direction: "prev" | "next") {
    setOffset(current => direction === "next" ? current + limit : Math.max(0, current - limit));
  }

  const selectedLink = analytics?.top_links.find(link => link.url === selectedTopLink);

  return <Page
    title={t(pageTitle())}
    subtitle={t(pageSubtitle())}
    primaryAction={{
      content: <div>
        <FontAwesomeIcon icon={faCircleUp} className="mr-2" />
        {t('Export List')}
      </div>,
      action: fetchExport,
    }}
    breadcrumbs={[
      { label: t("Email marketing"), url: "/admin/email-marketing" },
      { label: campaign?.name ?? 'Campaign', url: `/admin/email-marketing/campaigns/${campaignId}` },
      { label: t(pageTitle()) },
    ]}
  >
    <div className="flex items-center gap-2 mb-4">
      <div className="flex-1 min-w-0">
        <Search
          className="bg-white"
          searchCallback={handleSearch}
          historyKey={`email-campaign-${campaignId}-${metric}-recipients`}
          placeholder={t("Search by name, email...")}
        />
      </div>
      {metric === "top_links" && analytics?.top_links.length ? (
        <TopLinksFilter
          links={analytics.top_links}
          selected={selectedLink}
          onChange={url => {
            setSelectedTopLink(url);
            setOffset(0);
          }}
        />
      ) : null}
    </div>

    <DataTable
      columns={columns}
      loading={loading}
      sort={handleSort}
      footer={{
        tableLimit: limit,
        tableOffset: offset,
        handleTableOffset: handlePage,
        disableNextOffset: offset + limit >= total,
      }}
    >
      {!loading && recipients.length === 0 ? (
        <tr>
          <td colSpan={columns.length} className="text-center">
            {t("No recipients found.")}
          </td>
        </tr>
      ) : null}
      {!loading && recipients.map(recipient => (
        <tr key={recipient.id}>
          {renderCells(recipient)}
        </tr>
      ))}
    </DataTable>
  </Page>;
}
